import { api } from '@http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '@eye-able/ui';
import type {
  CreatePolicyBody,
  PolicyResponse,
} from '@http/queries/useCreatePolicy';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

/** Request body accepted by `PUT /policies` — the create fields plus the id. */
export interface UpdatePolicyBody extends CreatePolicyBody {
  id: number;
}

/**
 * Build the `PUT /policies` body that changes a policy's status, carrying over
 * the rest of its fields unchanged. Used by the archive/unarchive hooks.
 */
export function toPolicyStatusUpdateBody(
  policy: PolicyResponse,
  status: PolicyResponse['status'],
): UpdatePolicyBody {
  return {
    id: policy.id,
    name: policy.name,
    description: policy.description,
    assetType: policy.assetType,
    violationLabel: policy.violationLabel,
    priority: policy.priority,
    teamId: policy.teamId,
    domainScope: policy.domainScope,
    auditEntryIds: policy.auditEntryIds,
    rule: policy.rule,
    status,
  };
}

/** Update an existing custom governance policy (`PUT /policies`). */
export function useUpdatePolicy() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t } = useInlineTranslation();

  return useMutation({
    mutationFn: (body: UpdatePolicyBody) =>
      fetchWrapper.put<PolicyResponse>(api.policies, body),
    onSuccess: async (policy) => {
      // Changing a policy's definition deletes its open issues server-side,
      // so the issue lists and overview counts must be refetched too.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [api.policies] }),
        queryClient.invalidateQueries({ queryKey: [api.governanceIssues] }),
        queryClient.invalidateQueries({ queryKey: [api.governanceOverview] }),
      ]);
      notification.success(t('policyUpdatedSuccess'));
      return policy;
    },
  });
}
